import { useState, type ReactNode } from "react";
import { Skeleton, cn } from "@strivelabs/ui-core";
import { FileText, Loader2, Zap, X } from "lucide-react";
import type { ComposerAttachmentItem } from "./types";
import { COMPOSER_MODE_ACCENT, COMPOSER_MODE_ACCENT_RGB } from "./tones";

/** Context row above the input holding the skill chip and attachment chips. */
export const ComposerChips = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("flex flex-wrap gap-2 px-3 pt-3", className)}>
    {children}
  </div>
);

export interface ComposerSkillChipProps {
  label: string;
  onRemove?: () => void;
}

/**
 * The active-skill chip — tinted rather than filled: the mode accent at low
 * alpha for the ground and the edge, full strength for icon, label and the
 * always-visible remove control (capsule corners, per the redesign artifact).
 */
export const ComposerSkillChip = ({ label, onRemove }: ComposerSkillChipProps) => (
  <div
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border py-1 pl-2.5",
      onRemove ? "pr-1.5" : "pr-2.5",
      "duration-150 animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none",
    )}
    style={{
      color: COMPOSER_MODE_ACCENT,
      borderColor: `rgba(${COMPOSER_MODE_ACCENT_RGB},0.45)`,
      backgroundColor: `rgba(${COMPOSER_MODE_ACCENT_RGB},0.10)`,
    }}
  >
    <Zap className="size-3" />
    <span className="text-xs font-medium">{label}</span>
    {onRemove && (
      // Always visible — no hover reveal, so nothing about the chip changes
      // size or position between states. 18px box with a 12px glyph to stay
      // easy to hit.
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove skill"
        className="flex size-[18px] items-center justify-center rounded-full text-current opacity-70 transition-[opacity,background-color] duration-150 hover:bg-[rgba(245,107,16,0.14)] hover:opacity-100"
      >
        <X className="size-3" strokeWidth={2.6} />
      </button>
    )}
  </div>
);

export interface ComposerAttachmentProps {
  item: ComposerAttachmentItem;
  onRemove?: (id: string) => void;
  onOpen?: (id: string) => void;
}

/**
 * The chip's picture: a shimmer until the thumbnail has arrived, then the
 * thumbnail fading in, so it never pops in from an empty square (a stored
 * file's thumbnail comes from the server). One that cannot be loaded falls
 * back to the file mark, as a chip with no picture shows.
 */
const ChipThumbnail = ({ src }: { src: string }) => {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (failedSrc === src) {
    return <FileText className="size-[15px] text-muted-foreground" />;
  }

  const loaded = loadedSrc === src;
  return (
    <>
      {!loaded && <Skeleton className="absolute inset-0 size-full rounded" />}
      <img
        src={src}
        alt=""
        className={cn(
          "size-full object-cover transition-opacity duration-200 motion-reduce:transition-none",
          loaded ? "opacity-100" : "opacity-0",
        )}
        onLoad={() => setLoadedSrc(src)}
        onError={() => setFailedSrc(src)}
      />
    </>
  );
};

/**
 * A single attachment chip. Shown from the moment a file is picked: while it
 * uploads its picture carries a spinner and its second line says so, and a
 * failed upload stays, saying why, until it is removed.
 */
export const ComposerAttachment = ({
  item,
  onRemove,
  onOpen,
}: ComposerAttachmentProps) => (
  <div
    className={cn(
      "flex items-center gap-2 rounded-md border bg-muted/40 py-1 pl-1 pr-2 transition-colors duration-150 animate-in fade-in-0 zoom-in-95 hover:bg-accent motion-reduce:animate-none",
      item.status === "error" && "border-destructive/40 bg-destructive/5",
    )}
  >
    <button
      type="button"
      onClick={() => onOpen?.(item.id)}
      className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded bg-muted"
    >
      {item.kind === "image" && item.thumbUrl ? (
        <ChipThumbnail src={item.thumbUrl} />
      ) : (
        <FileText className="size-[15px] text-muted-foreground" />
      )}
      {item.status === "uploading" && (
        <span className="absolute inset-0 flex items-center justify-center bg-background/60">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </span>
      )}
    </button>
    <div className="min-w-0 max-w-[150px]">
      <div className="truncate text-xs font-medium">{item.name}</div>
      {item.status === "error" ? (
        <div
          title={item.error ?? "Upload failed"}
          className={cn(
            "truncate text-[10px] text-destructive",
            !item.error && "uppercase tracking-wide",
          )}
        >
          {item.error ?? "Upload failed"}
        </div>
      ) : item.status === "uploading" ? (
        <div role="status" className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Uploading…
        </div>
      ) : (
        item.meta && (
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {item.meta}
          </div>
        )
      )}
    </div>
    {onRemove && (
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        aria-label="Remove attachment"
        className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="size-3" strokeWidth={2.4} />
      </button>
    )}
  </div>
);

export interface ComposerAttachmentsProps {
  items: ComposerAttachmentItem[];
  onRemove?: (id: string) => void;
  onOpen?: (id: string) => void;
}

/** Renders the attachment chips for the given items. */
export const ComposerAttachments = ({
  items,
  onRemove,
  onOpen,
}: ComposerAttachmentsProps) => (
  <>
    {items.map((item) => (
      <ComposerAttachment
        key={item.id}
        item={item}
        onRemove={onRemove}
        onOpen={onOpen}
      />
    ))}
  </>
);
